/**
 * URQL hooks for ticket type CRUD operations.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";

// =============================================================================
// MUTATION DOCUMENTS
// =============================================================================

const CreateTicketTypeDocument = `
  mutation CreateTicketType($eventId: ID!, $input: TicketTypeInput!) {
    createTicketType(eventId: $eventId, input: $input) {
      id
      name
      description
      maxQuantity
      soldCount
      isActive
      prices { id currency price }
      price
      currency
    }
  }
`;

const UpdateTicketTypeDocument = `
  mutation UpdateTicketType($id: ID!, $input: TicketTypeUpdateInput!) {
    updateTicketType(id: $id, input: $input) {
      id
      name
      description
      maxQuantity
      soldCount
      isActive
      prices { id currency price }
      price
      currency
    }
  }
`;

const DeleteTicketTypeDocument = `
  mutation DeleteTicketType($id: ID!) {
    deleteTicketType(id: $id)
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface TicketTypePrice {
  currency: string;
  price: number;
}

interface CreateTicketTypeInput {
  name: string;
  description?: string | null;
  prices: TicketTypePrice[];
  maxQuantity?: number | null;
  sortOrder?: number;
  isActive?: boolean;
}

interface UpdateTicketTypeInput {
  name?: string;
  description?: string | null;
  prices?: TicketTypePrice[];
  maxQuantity?: number | null;
  isActive?: boolean;
  serviceFeeConfigId?: string | null;
  serviceFeePrices?: Array<{ currency: string; amount: string }>;
}

interface TicketType {
  id: string;
  name: string;
  description: string | null;
  maxQuantity: number | null;
  soldCount: number;
  isActive: boolean;
  prices: Array<{ id: string; currency: string; price: string }>;
  price: string | null;
  currency: string | null;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook for creating a new ticket type.
 */
export function useCreateTicketType() {
  const [result, executeMutation] = useMutation<
    { createTicketType: TicketType },
    { eventId: string; input: CreateTicketTypeInput }
  >(CreateTicketTypeDocument);

  const createTicketType = useCallback(
    async (
      eventId: string,
      input: CreateTicketTypeInput
    ): Promise<TicketType> => {
      try {
        const response = await executeMutation({ eventId, input });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Не удалось создать тип билета");
          const err = new Error(message);
          throw err;
        }

        if (!response.data?.createTicketType) {
          throw new Error("Сервер не вернул данные");
        }

        const ticketType = response.data.createTicketType;

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Ticket type created",
          data: { ticketTypeId: ticketType.id, eventId },
        });

        return ticketType;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Не удалось создать тип билета");
        errorTracking.captureError(error, {
          tags: { feature: "admin", action: "createTicketType" },
          extra: { eventId },
        });
        throw error;
      }
    },
    [executeMutation]
  );

  return {
    createTicketType,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

/**
 * Hook for updating an existing ticket type.
 */
export function useUpdateTicketType() {
  const [result, executeMutation] = useMutation<
    { updateTicketType: TicketType },
    { id: string; input: UpdateTicketTypeInput }
  >(UpdateTicketTypeDocument);

  const updateTicketType = useCallback(
    async (id: string, input: UpdateTicketTypeInput): Promise<TicketType> => {
      try {
        const response = await executeMutation({ id, input });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Не удалось обновить тип билета");
          const err = new Error(message);
          throw err;
        }

        if (!response.data?.updateTicketType) {
          throw new Error("Сервер не вернул данные");
        }

        const ticketType = response.data.updateTicketType;

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Ticket type updated",
          data: { ticketTypeId: ticketType.id },
        });

        return ticketType;
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error("Не удалось обновить тип билета");
        errorTracking.captureError(error, {
          tags: { feature: "admin", action: "updateTicketType" },
          extra: { ticketTypeId: id },
        });
        throw error;
      }
    },
    [executeMutation]
  );

  return {
    updateTicketType,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}

/**
 * Hook for deleting a ticket type.
 */
export function useDeleteTicketType() {
  const [result, executeMutation] = useMutation<
    { deleteTicketType: boolean },
    { id: string }
  >(DeleteTicketTypeDocument);

  const deleteTicketType = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const response = await executeMutation({ id });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Не удалось удалить тип билета");
          const err = new Error(message);
          throw err;
        }

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Ticket type deleted",
          data: { ticketTypeId: id },
        });

        return response.data?.deleteTicketType ?? true;
      } catch (err) {
        const error =
          err instanceof Error ? err : new Error("Не удалось удалить тип билета");
        errorTracking.captureError(error, {
          tags: { feature: "admin", action: "deleteTicketType" },
          extra: { ticketTypeId: id },
        });
        throw error;
      }
    },
    [executeMutation]
  );

  return {
    deleteTicketType,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
